import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { AvailabilityMode, EventTypeAccess, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAccountSchedulingSettingsQuery, useGetSchedulingLocationsQuery } from '../../../store/scheduling/scheduling'
import { PlaceToggleCards } from '../flows/form/PlaceToggleCards'
import { buildEventTypeLocationOptions, DURATION_OPTIONS, eventTypeLocationPatch, toBookingSlug } from './eventTypeOptions'
import { useDefaultLocationPreviews } from './useDefaultLocationPreviews'

interface EventTypeCreateModalProps {
  access: EventTypeAccess
  isCreating: boolean
  onCancel: () => void
  onCreate: (eventType: SchedulingEventType) => void
}

const COPY: Record<EventTypeAccess, { title: string; subtitle: string }> = {
  [EventTypeAccess.ACCOUNT]: {
    title: 'Create shared event type',
    subtitle: 'Visible to everyone in your account. Uses account defaults you can change later.',
  },
  [EventTypeAccess.PERSONAL]: {
    title: 'Create personal event type',
    subtitle: 'Only you can offer this event type. Uses account defaults you can change later.',
  },
}

export const EventTypeCreateModal: FC<EventTypeCreateModalProps> = ({ access, isCreating, onCancel, onCreate }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const availableLocations = locations ?? []
  const defaultLocation = availableLocations.find((location) => location.id === accountSettings?.defaults.locationId)
  const { accountDefaultLocation, userDefaultLocation } = useDefaultLocationPreviews(availableLocations)
  const locationOptions = buildEventTypeLocationOptions(availableLocations, accountDefaultLocation, userDefaultLocation)

  const [name, setName] = useState('')
  const [durationMinutes, setDurationMinutes] = useState(30)
  const [meetingMode, setMeetingMode] = useState<MeetingMode>(MeetingMode.VIRTUAL)
  const [locationValue, setLocationValue] = useState('')
  const [isActive, setIsActive] = useState(true)

  const isInPerson = meetingMode === MeetingMode.IN_PERSON

  const reset = () => {
    setName('')
    setDurationMinutes(30)
    setMeetingMode(MeetingMode.VIRTUAL)
    setLocationValue('')
    setIsActive(true)
  }

  const handleCancel = () => {
    reset()
    onCancel()
  }

  const handleCreate = () => {
    onCreate({
      id: '',
      name: name.trim(),
      slug: toBookingSlug(name),
      durationMinutes,
      meetingMode,
      clientFacingText: '',
      active: isActive,
      access,
      location: isInPerson ? eventTypeLocationPatch(locationValue).location : null,
      availabilityMode: AvailabilityMode.ACCOUNT_DEFAULT,
      bufferBeforeMinutes: 0,
      bufferAfterMinutes: 0,
      minimumNoticeMinutes: 1440,
      bookingWindowDays: 60,
      addToUserPagesByDefault: false,
      usedByCount: 0,
      updatedAt: '',
    })
  }

  const copy = COPY[access]

  return (
    <Modal
      show
      onHide={handleCancel}
      header={
        <div className='d-flex flex-column zocks-gap-0.25'>
          <h5 className='mb-0'>{copy.title}</h5>
          <span className='paragraph-small zocks-text-gray-600'>{copy.subtitle}</span>
        </div>
      }
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='event-type-name'>
              Name
            </label>
            <span className='paragraph-small zocks-text-gray-600'>Clients see this when they book.</span>
            <Form.Control
              id='event-type-name'
              type='text'
              value={name}
              placeholder='e.g. Annual review'
              onChange={(event) => setName(event.currentTarget.value)}
            />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='event-type-duration'>
              Duration
            </label>
            <Form.Select id='event-type-duration' value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.currentTarget.value))}>
              {DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} minutes
                </option>
              ))}
            </Form.Select>
          </div>
          <PlaceToggleCards
            meetingMode={meetingMode}
            defaultLocation={defaultLocation}
            onChange={(mode, locId) => {
              setMeetingMode(mode)
              setLocationValue(locId ?? '')
            }}
          />
          {isInPerson ? (
            <div className='d-flex flex-column zocks-gap-0.25'>
              <label className='bold' htmlFor='event-type-location'>
                Location
              </label>
              {availableLocations.length > 0 ? (
                <Form.Select id='event-type-location' value={locationValue} onChange={(event) => setLocationValue(event.currentTarget.value)}>
                  <option value=''>Select a location</option>
                  {locationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <span className='paragraph-small zocks-text-gray-600'>Add a location under Availability before offering in-person event types.</span>
              )}
            </div>
          ) : null}
          <div className='d-flex align-items-start justify-content-between gap-3'>
            <div className='d-flex flex-column'>
              <span className='bold'>Set status to active</span>
              <span className='paragraph-small zocks-text-gray-600'>When active, everyone in your account can add this event type to their booking page.</span>
            </div>
            <Form.Switch checked={isActive} onChange={(event) => setIsActive(event.currentTarget.checked)} />
          </div>
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle='Create event type'
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionDisabled={!name.trim() || (isInPerson && !locationValue)}
      footerPrimaryActionLoading={isCreating}
      onFooterPrimaryAction={handleCreate}
      onFooterSecondaryAction={handleCancel}
    />
  )
}
